import { Accordion, AccordionDetails, Stack } from '@mui/material';
import type { DynamicLineItemMember, DynamicLineItemPeriod } from '@vy/dynamic-invoice-builder';
import { notDefined } from '@vy/null';
import type { UsagePricingModel } from '@vy/types';
import { isLengthGreaterThan, isNotNullOrUndefined, vyDate } from '@vy/utils';
import { FC, useMemo } from 'react';
import { VyDivider } from '../../../../../components/new-design/atoms';
import { useDynamicInvoiceIds } from '../../hooks/use-dynamic-invoice-ids';
import { useUsagePeriodSelection } from '../hooks/use-usage-period-selection';
import type { UsageItemAccordionProps } from '../types';
import { isTieredMember } from '../utils/dynamic-line-item-formatters';
import { GroupedLineItemContent } from './GroupedLineItemContent';
import { PeriodSelector } from './PeriodSelector';
import { COLORS, usageItemAccordionStyles } from './styles';
import { dynamicInvoiceContentSelectors, getSelectorAttribute } from '@vy/app-selectors';
import { UsageDataTable } from './UsageDataTable';
import { UsageItemChart } from './UsageItemChart';
import { UsageItemSummary } from './UsageItemSummary';

export const UsageItemAccordion: FC<UsageItemAccordionProps> = ({
  lineItem,
  defaultExpanded = false,
  measurementChartData,
  customerId,
  billingCycle,
  currency,
  baseCurrency
}) => {
  const { invoiceId, customerId: contextCustomerId, reviewStatus } = useDynamicInvoiceIds();

  const members: DynamicLineItemMember<UsagePricingModel>[] = lineItem.dynamicLineItemMembers;
  const isGrouped = members.length > 1;
  const firstMember = members.at(0);
  const periods: DynamicLineItemPeriod<UsagePricingModel>[] = firstMember?.dynamicLineItemPeriods ?? [];
  const pricingModel = lineItem.pricingModel;
  const isRevShare = firstMember && isTieredMember(firstMember) && firstMember.isRevShare;

  const { selectedPeriodIndex, handlePeriodChange, currentPeriod } = useUsagePeriodSelection(periods);
  const hasBillingPeriods = isLengthGreaterThan(periods, 1);

  const chartData = useMemo(() => {
    if (notDefined(firstMember?.meterInfo) || notDefined(measurementChartData)) {
      return null;
    }

    const meterInfo = firstMember.meterInfo;
    for (const productId of Object.keys(measurementChartData)) {
      const productMeterData = measurementChartData[productId];
      const meterData = productMeterData?.[meterInfo.meterId];
      if (meterData) {
        return {
          meterData: { [meterInfo.meterId]: meterData },
          meterName: meterInfo.name,
          meterUnits: meterInfo.unitsType,
          aggregationOperator: meterInfo.aggregationOperator,
        };
      }
    }
    return null;
  }, [firstMember, measurementChartData]);

  const selectedPeriodRange = useMemo(() => {
    if (!currentPeriod) {
      return undefined;
    }

    return {
      from: vyDate(currentPeriod.fromDate).toDate(),
      to: vyDate(currentPeriod.toDate).toDate(),
    };
  }, [currentPeriod]);

  const effectiveChartPeriod = currentPeriod
    ? { startTime: vyDate(currentPeriod.fromDate).toDate(), endTime: vyDate(currentPeriod.toDate).toDate() }
    : billingCycle;

  const shouldShowChart =
    isNotNullOrUndefined(chartData) &&
    isNotNullOrUndefined(chartData.meterData) &&
    isNotNullOrUndefined(chartData.meterName) &&
    isNotNullOrUndefined(chartData.aggregationOperator);

  if (isGrouped) {
    return (
      <Accordion defaultExpanded={defaultExpanded} sx={usageItemAccordionStyles.accordion} {...getSelectorAttribute(dynamicInvoiceContentSelectors.usageLineItem)}>
        <UsageItemSummary
          lineItem={lineItem}
          invoiceId={invoiceId}
          customerId={contextCustomerId}
          reviewStatus={reviewStatus}
          currency={currency}
          aggregationOperator={firstMember?.meterInfo?.aggregationOperator}
        />
        <AccordionDetails sx={{ padding: 0 }}>
          {hasBillingPeriods && (
            <Stack sx={{
              m: "6px 24px 0 24px"
            }}>
              <PeriodSelector
                periods={periods}
                selectedPeriodIndex={selectedPeriodIndex}
                onPeriodChange={handlePeriodChange}
              />
            </Stack>
          )}

          {members.map((member, index) => (
            <Stack key={member.variantId} sx={{
              mt: index > 0 ? 4 : 0
            }}>
              {index > 0 && <VyDivider sx={{ mb: 4 }} />}
              <GroupedLineItemContent
                member={member}
                pricingModel={lineItem.pricingModel}
                selectedPeriodIndex={selectedPeriodIndex}
                measurementChartData={measurementChartData}
                billingCycle={billingCycle}
                currency={baseCurrency}
                customerId={customerId}
              />
            </Stack>
          ))}
        </AccordionDetails>
      </Accordion>
    );
  }

  // Non-grouped usage items rendering
  return (
    <Accordion defaultExpanded={defaultExpanded} sx={usageItemAccordionStyles.accordion} {...getSelectorAttribute(dynamicInvoiceContentSelectors.usageLineItem)}>
      <UsageItemSummary
        lineItem={lineItem}
        invoiceId={invoiceId}
        customerId={contextCustomerId}
        reviewStatus={reviewStatus}
        currency={currency}
        aggregationOperator={firstMember?.meterInfo?.aggregationOperator}
      />
      <AccordionDetails sx={{ padding: 0 }}>
        {periods.length > 0 && (
          <Stack
            sx={{
              m: "6px 24px 24px 24px",
              border: `1px solid ${COLORS.border}`,
              borderRadius: "8px"
            }}>
            {hasBillingPeriods && (
              <Stack>
                <PeriodSelector
                  periods={periods}
                  selectedPeriodIndex={selectedPeriodIndex}
                  onPeriodChange={handlePeriodChange}
                />
                <UsageDataTable
                  period={currentPeriod}
                  pricingModel={pricingModel}
                  currency={baseCurrency}
                  isRevShare={isRevShare}
                  aggregationOperator={firstMember?.meterInfo?.aggregationOperator}
                />
              </Stack>
            )}
            {!hasBillingPeriods && (
              <UsageDataTable
                period={currentPeriod}
                pricingModel={pricingModel}
                currency={baseCurrency}
                isRevShare={isRevShare}
                hasBillingPeriods={false}
                aggregationOperator={firstMember?.meterInfo?.aggregationOperator}
              />
            )}
          </Stack>
        )}

        {shouldShowChart && chartData && (
          <UsageItemChart
            meterData={chartData.meterData}
            meterName={chartData.meterName}
            meterUnits={chartData.meterUnits}
            aggregationOperator={chartData.aggregationOperator}
            period={effectiveChartPeriod}
            currency={currency}
            customerId={customerId}
            selectedPeriodRange={selectedPeriodRange}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
};
